import React, { useMemo, useRef, useLayoutEffect } from 'react';
import type { IntlShape } from 'react-intl';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { DecorationSet, type NodeView } from '@atlaskit/editor-prosemirror/view';
import type { NodeViewConstructor } from '@atlaskit/editor-common/lazy-node-view';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import { DOMSerializer, type Mark, type Schema } from '@atlaskit/editor-prosemirror/model';
import type { EditorPlugin } from '@atlaskit/editor-common/types';
import type { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import {
	profileSSROperation,
	SSRRenderMeasure,
} from '@atlaskit/editor-common/performance/ssr-measures';
import { isSSRStreaming } from '@atlaskit/editor-common/core-utils';
import { createSSRPMPlugins } from './create-ssr-pm-plugins';
import { createSSREditorState } from './create-ssr-editor-state';

const SSR_TRACE_SEGMENT_NAME = 'reactEditorView/editorSSRRenderer';

// The copy of type from prosemirror-view.
// Probably, we need to fix this package exports and add `NodeViewConstructor` and `MarkViewConstructor` types here.
type MarkViewConstructor = (mark: Mark, view: EditorView, inline: boolean) => NodeView;

interface Props {
	'aria-describedby'?: string;
	'aria-label': string;
	className: string;
	'data-editor-id': string;
	doc: PMNode | undefined;
	id: string;
	intl: IntlShape;
	onEditorStateChanged?: (state: EditorState) => void;
	onSSRMeasure?: (measure: {
		endTimestamp: number;
		segmentName: string;
		startTimestamp: number;
	}) => void;
	plugins: EditorPlugin[];
	portalProviderAPI: PortalProviderAPI;
	/**
	 * Pre-built EditorState from ReactEditorView's ssrDeps.
	 * When provided, skips internal EditorState creation to avoid double work.
	 */
	prebuiltEditorState?: EditorState;
	/**
	 * Pre-built SafePlugins from ReactEditorView's ssrDeps.
	 * When provided, skips internal PM plugin creation to avoid double work.
	 */
	prebuiltPMPlugins?: SafePlugin[];
	schema: Schema;
}

/**
 * A lightweight EditorView implementation for SSR environments.
 *
 * It's kind of aggressive right now, we need to test and see how it works.
 * Probably, we will need to implement more methods/properties in the future.
 *
 * If it doesn't work, we can extend `EditorView` directly instead of implementing `Partial<EditorView>`,
 * but it will perform some DOM operation during the construction.
 */
class SSREditorView implements Pick<EditorView, keyof EditorView> {
	readonly state: EditorState;
	readonly dom: HTMLElement;
	readonly editable: boolean;
	readonly dragging: null;
	readonly composing: boolean;
	readonly props: { state: EditorState };
	readonly root: Document;
	readonly isDestroyed: boolean;

	update() {
		// No-op in SSR
	}
	setProps() {
		// No-op in SSR
	}
	dispatchEvent() {
		// No-op in SSR
	}
	dispatch() {
		// No-op in SSR
	}
	hasFocus() {
		return false;
	}
	focus() {
		// No-op in SSR
	}
	updateRoot() {
		// No-op in SSR
	}
	posAtCoords() {
		return null;
	}
	coordsAtPos() {
		return { left: 0, right: 0, top: 0, bottom: 0 };
	}
	domAtPos() {
		return { node: this.root, offset: 0 };
	}
	nodeDOM() {
		return null;
	}
	posAtDOM() {
		return 0;
	}
	endOfTextblock() {
		return false;
	}
	pasteHTML() {
		return false;
	}
	pasteText() {
		return false;
	}
	destroy() {
		// No-op in SSR
	}
	updateState() {
		// No-op in SSR
	}
	someProp() {
		return undefined;
	}

	serializeForClipboard(slice: Slice): { dom: HTMLElement; slice: Slice; text: string } {
		// No-op in SSR - clipboard operations are not supported
		return {
			dom: document.createElement('div'),
			text: '',
			slice,
		};
	}

	constructor(place: null, props: { state: EditorState }) {
		this.state = props.state;
		this.dom = document.createElement('div');
		this.editable = true;
		this.dragging = null;
		this.composing = false;
		this.props = props;
		this.root = document;
		this.isDestroyed = false;
	}
}

export function EditorSSRRenderer({
	plugins,
	schema,
	doc,
	portalProviderAPI,
	intl,
	onSSRMeasure,
	onEditorStateChanged,
	prebuiltPMPlugins,
	prebuiltEditorState,
	...divProps
}: Props): React.JSX.Element {
	// Should be always the first statement in the component
	const firstRenderStartTimestampRef = useRef(performance.now());

	// PMPluginFactoryParams use `getIntl` function to get current intl instance,
	// so we don't need to add `intl` as a dependency to `useMemo`.
	// We will store intl in ref and access to it dynamically in `getIntl` function call.
	const intlRef = useRef(intl);
	intlRef.current = intl;

	const pmPlugins = useMemo(() => {
		if (prebuiltPMPlugins) {
			return prebuiltPMPlugins;
		}
		return profileSSROperation(
			`${SSR_TRACE_SEGMENT_NAME}/createPMPlugins`,
			() =>
				createSSRPMPlugins({ plugins, schema, portalProviderAPI, getIntl: () => intlRef.current }),
			onSSRMeasure,
		);
	}, [plugins, portalProviderAPI, schema, onSSRMeasure, prebuiltPMPlugins]);

	const nodeViews = useMemo(() => {
		return pmPlugins.reduce<Record<string, NodeViewConstructor>>((acc, plugin) => {
			return Object.assign(acc, plugin.props.nodeViews);
		}, {});
	}, [pmPlugins]);

	const markViews = useMemo(() => {
		return pmPlugins.reduce<Record<string, MarkViewConstructor>>((acc, plugin) => {
			return Object.assign(acc, plugin.props.markViews);
		}, {});
	}, [pmPlugins]);

	const editorState = useMemo(() => {
		if (prebuiltEditorState) {
			return prebuiltEditorState;
		}

		return profileSSROperation(
			`${SSR_TRACE_SEGMENT_NAME}/createSSREditorState`,
			() => createSSREditorState({ doc, schema, pmPlugins }),
			onSSRMeasure,
		);
	}, [doc, pmPlugins, schema, onSSRMeasure, prebuiltEditorState]);

	// In React 19 could be replaced by `useEffectEvent` hook.
	const onEditorStateChangedRef = useRef(onEditorStateChanged);
	onEditorStateChangedRef.current = onEditorStateChanged;
	useLayoutEffect(() => {
		onEditorStateChangedRef.current?.(editorState);
	}, [editorState]);

	const editorView = useMemo(() => {
		return new SSREditorView(null, {
			state: editorState,
		}) as unknown as EditorView;
	}, [editorState]);

	const { serializer, nodePositions } = useMemo(() => {
		const createSerializerAndNodePositions = () => {
			const nodePositions = new WeakMap<PMNode, number>();

			// ProseMirror View adds <br class="ProseMirror-trailingBreak" /> to empty nodes. Because we are using
			// DOMSerializer, we should simulate the same behaviour to get the same HTML document.
			//
			// There are a lot of conditions that check for adding `<br />` but we could implement only the case when we
			// are adding `<br />` to empty texblock, because if we add `<br />` in other cases it will change order of DOM nodes inside
			// this node (`<br />`) will be the first, after will be other nodes. It's because we are adding `<br />` to root node before
			// we are rendering child node.
			//
			// See: https://discuss.prosemirror.net/t/where-can-i-read-about-prosemirror-trailingbreak/6665
			// See: https://github.com/ProseMirror/prosemirror-view/blob/76c7c47f03730b18397b94bd269ece8a9cb7f486/src/viewdesc.ts#L803
			// See: https://github.com/ProseMirror/prosemirror-view/blob/76c7c47f03730b18397b94bd269ece8a9cb7f486/src/viewdesc.ts#L1365
			const addTrailingBreakIfNeeded = (
				node: PMNode,
				contentDOM: HTMLElement | null | undefined,
			) => {
				if (contentDOM && node.isTextblock && !node.lastChild) {
					const br = document.createElement('br');
					br.classList.add('ProseMirror-trailingBreak');
					contentDOM.appendChild(br);
				}
			};

			const toDomNodeRenderers = Object.fromEntries(
				Object.entries(schema.nodes)
					.map(([nodeName, nodeType]) => {
						return [nodeName, nodeType.spec.toDOM];
					})
					.filter(([, toDOM]) => !!toDOM),
			);
			const toDomMarkRenderers = Object.fromEntries(
				Object.entries(schema.marks)
					.map(([markName, markType]) => {
						return [markName, markType.spec.toDOM];
					})
					.filter(([, toDOM]) => !!toDOM),
			);

			const nodeViewRenderers = Object.fromEntries(
				Object.entries(nodeViews).map(([nodeName, nodeViewFactory]) => {
					return [
						nodeName,
						(node: PMNode) => {
							try {
								const nodeViewInstance = nodeViewFactory(
									node,
									editorView,
									() => nodePositions.get(node) ?? 0,
									[],
									DecorationSet.create(node, []),
								);

								addTrailingBreakIfNeeded(node, nodeViewInstance.contentDOM);

								return {
									dom: nodeViewInstance.dom,
									// Leaf nodes have no content, ProseMirror will throw an error if we pass contentDOM
									contentDOM: node.isLeaf ? undefined : nodeViewInstance.contentDOM,
								};
							} catch (error) {
								if (process.env.NODE_ENV !== 'production') {
									// eslint-disable-next-line no-console
									console.warn(
										`[EditorSSR] Failed to instantiate "${nodeName}" nodeView during SSR. ` +
											'Falling back to schema toDOM for this node.',
										error,
									);
								}

								// Fall back to schema-level toDOM for this node
								const toDOM = schema.nodes[nodeName]?.spec.toDOM;
								if (toDOM) {
									return DOMSerializer.renderSpec(document, toDOM(node));
								}
								// Last resort: empty div
								return { dom: document.createElement('div') };
							}
						},
					];
				}),
			);

			// Create renderers for textblock nodes that don't have custom NodeViews (e.g. paragraph, heading)
			const textblockRenderers = Object.fromEntries(
				Object.entries(schema.nodes)
					.filter(([nodeName, nodeType]) => {
						// Only handle textblock nodes
						return nodeType.spec.toDOM && nodeType.isTextblock && !nodeViews[nodeName];
					})
					.map(([nodeName, nodeType]) => {
						const toDOM = nodeType.spec.toDOM;
						if (!toDOM) {
							return [nodeName, undefined];
						}

						return [
							nodeName,
							(node: PMNode) => {
								if (!node.lastChild) {
									const result = DOMSerializer.renderSpec(document, toDOM(node));
									addTrailingBreakIfNeeded(node, result.contentDOM);
									return result;
								}

								return toDOM(node);
							},
						];
					})
					.filter(([, renderer]) => !!renderer),
			);

			const markViewRenderers = Object.fromEntries(
				Object.entries(markViews).map(([markName, markViewFactory]) => {
					return [
						markName,
						(mark: Mark) => {
							const markViewInstance = markViewFactory(mark, editorView, false);

							return {
								dom: markViewInstance.dom,
								contentDOM: markViewInstance.contentDOM,
							};
						},
					];
				}),
			);

			const serializer = new DOMSerializer(
				{
					...toDomNodeRenderers,
					...textblockRenderers,
					...nodeViewRenderers,
					text: renderText,
				},
				{
					...toDomMarkRenderers,
					...markViewRenderers,
				},
			);

			return { serializer, nodePositions };
		};

		return profileSSROperation(
			`${SSR_TRACE_SEGMENT_NAME}/createSerializerAndNodePositions`,
			createSerializerAndNodePositions,
			onSSRMeasure,
		);
	}, [editorView, markViews, nodeViews, schema.marks, schema.nodes, onSSRMeasure]);

	const editorHTML = useMemo(() => {
		const serializeFragment = () => {
			if (!doc) {
				return undefined;
			}

			doc.descendants((node, pos) => {
				nodePositions.set(node, pos);
			});

			if (isSSRStreaming()) {
				const fragment = serializer.serializeFragment(doc.content);
				const wrapper = document.createElement('div');
				wrapper.appendChild(fragment);
				return wrapper.innerHTML;
			} else {
				return serializer.serializeFragment(doc.content);
			}
		};

		try {
			return profileSSROperation(
				`${SSR_TRACE_SEGMENT_NAME}/serializeFragment`,
				serializeFragment,
				onSSRMeasure,
			);
		} catch (error) {
			if (process.env.NODE_ENV !== 'production') {
				// eslint-disable-next-line no-console
				console.warn('[EditorSSR] Failed to serialize editor document during SSR.', error);
			}
			return undefined;
		}
		// eslint-disable-next-line @atlassian/perf-linting/no-unstable-usememo-deps -- Ignored via go/ees017 (to be fixed)
	}, [doc, serializer, nodePositions, onSSRMeasure]);

	const containerRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!isSSRStreaming()) {
			if (containerRef.current && editorHTML && typeof editorHTML !== 'string') {
				containerRef.current.innerHTML = '';
				containerRef.current.appendChild(editorHTML);
			}
		}
	}, [editorHTML]);

	return (
		<SSRRenderMeasure
			segmentName={SSR_TRACE_SEGMENT_NAME}
			startTimestampRef={firstRenderStartTimestampRef}
			onSSRMeasure={onSSRMeasure}
		>
			<div
				ref={containerRef}
				id={divProps.id}
				// eslint-disable-next-line react/no-danger -- It's intentional by design
				dangerouslySetInnerHTML={
					typeof editorHTML === 'string' && isSSRStreaming() ? { __html: editorHTML } : undefined
				}
				// For some reason on SSR, the result `class` has a trailing space, that broke UFO,
				// because ReactEditorView produces a div with `class` without space.
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={divProps.className.trim()}
				aria-label={divProps['aria-label']}
				aria-describedby={divProps['aria-describedby']}
				data-editor-id={divProps['data-editor-id']}
				data-vc-ignore-if-no-layout-shift={true}
				data-ssr-placeholder="editor-view"
				data-ssr-placeholder-replace="editor-view"
				aria-multiline={true}
				role="textbox"
				// @ts-expect-error - contenteditable is not exist in div attributes
				contenteditable="true"
				data-gramm="false"
				translate="no"
			/>
		</SSRRenderMeasure>
	);
}

function renderText(node: PMNode) {
	return node.text || '';
}
